/**
 * StudySync Verification Service
 * Handles email and phone verification for participants
 */

import { api } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
import type { ApiResponse } from '../types';

export interface EmailVerificationRequest {
  participantId: string;
  email: string;
  studyCode: string;
  purpose?: 'app_reconnect' | 'google_health_enrollment';
}

export interface EmailCodeVerificationRequest {
  participantId: string;
  email: string;
  studyCode: string;
  verificationCode: string;
  purpose?: 'app_reconnect' | 'google_health_enrollment';
}

export interface ReconnectProof {
  proofId: string;
  proofToken: string;
  expiresAt: string;
}

export interface EmailCodeVerificationResponse {
  success: boolean;
  message?: string;
  verificationStatus: string;
  reconnectProof?: ReconnectProof;
  googleHealthEnrollmentSession?: GoogleHealthEnrollmentSession;
}

export interface GoogleHealthEnrollmentSession {
  accessToken: string;
  expiresAt: number;
}

export interface PhoneVerificationRequest {
  participantId: string;
  phoneNumber: string;
  studyCode: string;
  purpose?: 'google_health_enrollment';
}

export interface PhoneCodeVerificationRequest {
  participantId: string;
  phoneNumber: string;
  studyCode: string;
  verificationCode: string;
  purpose?: 'google_health_enrollment';
}

export interface PhoneCodeVerificationResponse {
  success: boolean;
  message?: string;
  verificationStatus: string;
  googleHealthEnrollmentSession?: GoogleHealthEnrollmentSession;
}

export interface VerificationStatus {
  participantId: string;
  email: string;
  studyCode: string;
  verificationStatus: 'pending' | 'email_verified' | 'phone_verified' | 'fully_verified' | 'failed';
  emailVerifiedAt?: string;
  phoneVerifiedAt?: string;
}

export class VerificationService {
  /**
   * Send email verification code to participant
   */
  static async sendEmailVerificationCode(
    request: EmailVerificationRequest
  ): Promise<ApiResponse<{ expiresAt: string }>> {
    return api.post(API_ENDPOINTS.VERIFICATION.SEND_EMAIL_CODE, request);
  }

  /**
   * Verify email verification code
   */
  static async verifyEmailCode(
    request: EmailCodeVerificationRequest
  ): Promise<EmailCodeVerificationResponse> {
    return api.post(API_ENDPOINTS.VERIFICATION.VERIFY_EMAIL_CODE, request);
  }

  /**
   * Send phone verification code to participant
   */
  static async sendPhoneVerificationCode(
    request: PhoneVerificationRequest
  ): Promise<ApiResponse<{ verificationSid: string }>> {
    return api.post(API_ENDPOINTS.VERIFICATION.SEND_PHONE_CODE, request);
  }

  /**
   * Verify phone verification code
   */
  static async verifyPhoneCode(
    request: PhoneCodeVerificationRequest
  ): Promise<PhoneCodeVerificationResponse> {
    return api.post(API_ENDPOINTS.VERIFICATION.VERIFY_PHONE_CODE, request);
  }

  /**
   * Get participant verification status
   */
  static async getVerificationStatus(
    participantId: string,
    email: string,
    studyCode: string
  ): Promise<ApiResponse<VerificationStatus>> {
    const params = new URLSearchParams({
      email,
      studyCode
    });
    
    return api.get(`${API_ENDPOINTS.VERIFICATION.STATUS}/${participantId}?${params}`);
  }

  /**
   * Validate verification code format
   */
  static validateVerificationCode(code: string): boolean {
    const codeRegex = /^\d{6}$/;
    return codeRegex.test(code);
  }

  /**
   * Format verification code for display
   */
  static formatVerificationCode(code: string): string {
    return code.replace(/(\d{3})(\d{3})/, '$1-$2');
  }
}

export default VerificationService;
